import React from "react";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string | null;
}

const RELOAD_KEY = "wn:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 60_000;

function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  return (
    e?.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module/i.test(
      e?.message ?? ""
    )
  );
}

function reloadOnceForStaleBuild(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * React error boundary that catches component tree crashes and reports them
 * to /api/client-error → Firestore systemEvents for visibility in the admin
 * monitoring dashboard. Renders a fallback UI instead of a blank page.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error?.message ?? "Unknown error" };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A chunk miss means this tab runs an older build than what is deployed.
    // Reload once (guarded so a genuinely broken deploy can't loop) to pick up
    // the current index.html and its chunk hashes.
    if (isChunkLoadError(error) && reloadOnceForStaleBuild()) return;

    fetch("/api/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "client.react-error-boundary",
        message: error?.message ?? "React component error",
        stack: error?.stack,
        componentStack: info?.componentStack ?? undefined,
        url: window.location.href,
      }),
    }).catch(() => {});
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <p>Something went wrong. Please refresh the page.</p>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
